import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async"; // For metadata management

// font awesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";

// components
import axiosInstance from "../../apis/config";
import SortDropdown from "../../components/shop/sortDropdown";
import PagePagination from "../../components/shop/pagePagination";
import Aside from "../../components/shop/aside";
import Spinner from "../../components/common/spinner";

// functions
import { disableBodyScroll, enableBodyScroll } from "../../functions/global";

// style
import style from "./shop.module.css";
import HomeProductCartShared from "../../components/common/homeProductCartShared";

const Shop = () => {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(50);
  const [isSmallScreen, SetIsSmallScreen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [searchObj, setSearchObj] = useState({
    page: 1,
    brand: "all",
    category: "all",
    sort: 0,
    price: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams({});
  const brands = useSelector((state) => state.brands.brands);
  const content = useRef();

  const getData = (
    getMaxMinPrices = false,
    page = 1,
    brand = searchObj.brand,
    category = searchObj.category,
    sort = searchObj.sort,
    price = searchObj.price
  ) => {
    setProducts(null);
    axiosInstance
      .get("/products", {
        params: {
          page,
          brand,
          category,
          sort,
          price,
        },
      })
      .then((res) => {
        const shuffledProducts = res.data.data.sort(() => Math.random() - 0.5);
        setProducts(shuffledProducts);
        setTotalPages(res.data.totalPages);
        if (getMaxMinPrices) {
          setMinPrice(res.data.minPrice);
          setMaxPrice(res.data.maxPrice);
          setSearchObj({
            page,
            brand,
            category,
            sort,
            price,
          });
          setSearchParams({
            page,
            brand,
            category,
            sort,
            price,
          });
        }
      })
      .catch((error) => console.log(error));
  };

  const updateFilters = (key, value) => {
    const obj = { ...searchObj };
    obj[key] = value;
    if (key !== "page") obj["page"] = 1;
    setSearchObj(obj);
    setSearchParams(obj);
  };

  const handlePageChange = (page) => {
    content.current.scrollIntoView({ behavior: "smooth" });
    updateFilters("page", page);
    getData(false, page);
  };

  const handleSort = (sorting) => {
    updateFilters("sort", sorting);
    getData(false, 1, searchObj.brand, searchObj.category, sorting);
  };

  const handleCategoryFilter = (value) => {
    updateFilters("category", value);
    getData(true, 1, searchObj.brand, value);
  };

  const handleBrandFilter = (value) => {
    updateFilters("brand", value);
    getData(true, 1, value);
  };

  const handlePriceFilter = (value) => {
    updateFilters("price", value);
    getData(
      false,
      1,
      searchObj.brand,
      searchObj.category,
      searchObj.sort,
      value
    );
  };

  const handleCloseFilterModal = () => {
    setShowFilterModal(false);
    enableBodyScroll();
  };

  const handleOpenFilterModal = () => {
    disableBodyScroll();
    setShowFilterModal(true);
  };

  useEffect(() => {
    if (searchParams.get("page")) {
      const page = +searchParams.get("page");
      const brand = searchParams.get("brand") || searchObj.brand;
      const category = searchParams.get("category") || searchObj.category;
      const sort = Number(searchParams.get("sort")) || searchObj.sort;
      const price = +searchParams.get("price") || searchObj.price;
      setSearchObj({ page, brand, category, sort, price });
      getData(true, page, brand, category, sort, price);
    } else {
      getData(true);
    }
    axiosInstance
      .get("/categories")
      .then((res) => {
        setCategories(res.data.data);
      })
      .catch((error) => console.log(error));
    // eslint-disable-next-line
  }, []);

  useLayoutEffect(() => {
    function updateSize() {
      SetIsSmallScreen(window.innerWidth < 768 ? true : false);
    }
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
    // eslint-disable-next-line
  }, [window.innerWidth]);

  // Generate SEO metadata
  const categoryName =
    categories.find((c) => c._id === searchObj.category)?.name ||
    "Security Products";
  const brandName =
    brands.find((b) => b._id === searchObj.brand)?.name || "All Brands";

  const pageTitle = `Security ${categoryName} in Nakuru, Kenya | Page ${searchObj.page}`;
  const pageDescription = `Shop ${brandName} ${categoryName} security systems in Nakuru. Find CCTV, biometrics, alarms and more at competitive prices.`;
  const pageKeywords = `security systems, Nakuru, Kenya, ${categoryName}, ${brandName}, CCTV, biometric, alarms`;

  // Generate structured data
  const structuredData = products
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: products.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "Product",
            name: product.name,
            url: `${window.location.origin}/product-details/${product._id}`,
            image: product.documents?.[0]?.url || "",
            offers: {
              "@type": "Offer",
              priceCurrency: "KES",
              price: product.price,
              availability: product.colors?.some((c) => c.stock > 0)
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            },
          },
        })),
      }
    : null;

  return (
    <section
      id="shop"
      className="py-5"
      itemScope
      itemType="https://schema.org/ItemList"
    >
      {/* SEO Metadata */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* Structured data for search engines */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      <meta itemProp="name" content={`${categoryName} Security Products`} />

      <div ref={content} className="container-fluid">
        <h1 className="sr-only">Security Products Shop in Nakuru, Kenya</h1>

        <div
          className={`${style["content-holder"]} row mx-0 mt-0 flex-md-row-reverse`}
        >
          <Aside
            showFilterModal={showFilterModal}
            isSmallScreen={isSmallScreen}
            closeFilterModal={handleCloseFilterModal}
            categories={categories}
            brands={brands}
            minPrice={minPrice}
            maxPrice={maxPrice}
            filters={searchObj}
            handleCategoryFilter={handleCategoryFilter}
            handleBrandFilter={handleBrandFilter}
            handlePriceFilter={handlePriceFilter}
          />
          <div className="col-lg-9 col-md-8 d-flex flex-column justify-content-between">
            {products ? (
              products.length > 0 ? (
                <>
                  <div className="products mb-5">
                    <div
                      className={`${
                        !isSmallScreen
                          ? "d-flex justify-content-between align-items-center"
                          : ""
                      } mb-4`}
                    >
                      <p className="mb-0">
                        Showing page {searchObj.page} of {totalPages} pages
                      </p>
                      {isSmallScreen ? (
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center pt-3">
                          <button
                            className="btn border d-inline-block mb-sm-0 mb-4"
                            type="button"
                            aria-expanded="false"
                            onClick={handleOpenFilterModal}
                            aria-label="Filter security products"
                          >
                            <FontAwesomeIcon icon={faFilter} /> Filters
                          </button>
                          <SortDropdown
                            active={searchObj.sort}
                            onSort={handleSort}
                          />
                        </div>
                      ) : (
                        <SortDropdown
                          active={searchObj.sort}
                          onSort={handleSort}
                        />
                      )}
                    </div>
                    <div className="row row-gap-3">
                      <HomeProductCartShared
                        products={products}
                        shop
                        itemProp="itemListElement"
                        itemScope
                        itemType="https://schema.org/Product"
                      />
                    </div>
                  </div>
                  <PagePagination
                    currentPage={searchObj.page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </>
              ) : (
                <p className="text-center h5 mt-5 pt-5">
                  Sorry, no matching security products found in Nakuru
                </p>
              )
            ) : (
              <Spinner message={"Loading security products..."} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Shop;
